/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import { Modal } from 'dig-components/modal';
import { Button } from 'dig-components/buttons';
import { Tooltip } from 'dig-components/dist/tooltips';
import { Spinner } from 'dig-components/dist/progress_indicators';
import { Template } from 'generated/types/HelloRequest';
import {
  clearTemplateSearch,
  fetchTemplatesThunk,
  searchTemplatesThunk,
  selectPageOfTemplates,
  selectTemplateSearch,
  templatePageThunk,
} from 'hellospa/page/templates/template-slice';
import { DocumentsTable, TableDataContainer, TableDataShape } from 'hellospa/components/docs/table';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, StoreShape } from 'hellospa/redux/types';
import styles from './select-template-modal.scss';
import { TabsAndSearch } from 'hellospa/page/templates/tabs-and-search';
import { defineMessages, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import Pagination from 'hellospa/foundation/tables/table/pagination';
import classNames from 'classnames';
import { ModalZeroState } from './modal-zero-state';
import { trackHeapCustomEvent } from 'common/utils/heap';

import { getTemplates, isBulkSend } from 'hellospa/page/prep-and-send/data/selectors';
import { SearchZeroState } from './search-zero-state';
import { isDisabled, MinimalTemplateResponse } from './template-disabled';
import unstarredZeroState from 'hellospa/page/templates/assets/unstarred-zero-state.svg';
import Spacer, { SpacerSize } from 'hellospa/foundation/spacing/spacer';

const messages = defineMessages({
  close: {
    id: '',
    description: 'Aria label for the close button on a modal',
    defaultMessage: 'Close',
  },
  zeroStateUnstarredHeader: {
    id: '',
    description: 'Zero state page header if no starred template',
    defaultMessage: 'You haven\'t starred any templates yet',
  },
});

type TemplateBaseShape = TableDataContainer<TableDataShape<{
  // I'm placing the whole template in a property because at the moment it has a
  // .guid that isn't the template's ID, that's .templateGuid.
  template: Template
  message?: MessageDescriptor,
}>>

const PAGE_SIZE = 20;

/**
 * It was getting hard to juggle all the variables and conditions, so I
 * extracted that complexity into this hook. Now the component only knows about
 * a few small items and doesn't have to try to figure out whether to use search
 * data or template page data.
 */
type UseTemplatesWithSearchProps = {
  isOpen: boolean,
  includeTemplateLinks: boolean,
}


export function useTemplatesWithSearch(
  { isOpen, includeTemplateLinks }: UseTemplatesWithSearchProps,
) {
  const dispatch = useDispatch<Dispatch>();
  const [isSearching, _setIsSearching] = React.useState(false);
  const templatePage = useSelector(selectPageOfTemplates);
  const search = useSelector(selectTemplateSearch);
  const templatesInRequest = useSelector(getTemplates);
  const setIsSearching = (searching: boolean) => {
    _setIsSearching(searching);
    if (!searching) {
      dispatch(clearTemplateSearch());
    }
  };


  const isLoading = (isSearching && search != null
    ? search.status === 'pending'
    : templatePage.isLoading
  );

  React.useEffect(() => {
    if (isOpen) {
      dispatch(fetchTemplatesThunk({
        starred: templatePage.starred,
        pageNum: 1,
        includeTemplateLinks,
      }));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = React.useMemo((): TemplateBaseShape | undefined => {
    if (isSearching) {
      if (search != null) {

        let numPages = 0;
        let resultsCount = 1;
        if (search.status === 'fulfilled') {
          numPages = Math.ceil(search.resultsCount / PAGE_SIZE);
          resultsCount = search.resultsCount;
        }

        return {
          numPages,
          resultsCount,
          currentPage: search.pageNum,
          pageResults: search.results.map((template) => {
            return {
              guid: template.guid,
              template,
            };
          }),
        };
      } else {
        return undefined;
      }
    }

    const nonSelectedTemplates = templatePage.templates.filter((template) => {
      return !templatesInRequest.some(
        (inRequestTemplate) => inRequestTemplate.templateGuid === template.guid,
      );
    });
    return {
      numPages: templatePage.numPages,
      resultsCount: templatePage.resultsCount,
      currentPage: templatePage.currentPage,
      pageResults: nonSelectedTemplates.map((template) => {
        return {
          guid: template.guid,
          template,
        };
      }),
    };
  }, [isSearching, search, templatePage, templatesInRequest]);
  async function gotoPage(pageNum: number) {
    if (isSearching && search) {
      await dispatch(searchTemplatesThunk({
        query: search.query,
        pageNum,
        includeTemplateLinks,
      })).unwrap();
    } else {
      await dispatch(templatePageThunk({ pageNum, includeTemplateLinks })).unwrap();
    }
  }
  return {
    includeTemplateLinks,
    isLoading,
    isSearching,
    setIsSearching,
    data,
    currentPage: templatePage.currentPage,
    dispatch,
    gotoPage,
    isStarZeroState: (
      templatePage.starred
      && !isLoading
      && data
      && data.resultsCount === 0
    ),
  };
}

export type Props = {
  isOpen: boolean,
  onRequestClose: () => void
  onSelected: (template: Template) => void
  title: React.ReactNode
  userCanCreateTemplates: boolean
  /**
   * I had intended to build SelectTemplateModal so that it could be used
   * anywhere. It uses its own Redux slice, it lets the caller decide what to do
   * with a selected template. The problem I ran into here is with validation.
   * SelectTemplateModal looks at P&S's slice to see what templates are already
   * in the request. That means the story that's supposed to JUST check the
   * SelectTemplateModal also has to have the P&S slice all setup. That wouldn't
   * be a problem, except P&S's template generator generates TemplateResponse
   * objects and TemplateSlice's generator makes Template objects.
   *
   * Everything stays in sync in the app because they're reading from the same
   * data source, but in tests and storybook we have 2 different functions that
   * generate a random (but deterministic) template based on the ID provided.
   * That means when a template is selected in SelectTemplateModal, and its ID
   * is passed over to the Prep&Send code, it's going to have a totally
   * different name and attributes. At the moment, I'm going to have the story
   * use this prop to override that selector.
   *
   * https://hellosign.atlassian.net/browse/DEV-17771
   * TODO: Investigate making one of the template generators power the other.
   * Maybe we always generate one format and convert between types.
   */
  storybookOverrideTemplatesInRequest?: MinimalTemplateResponse[],
}
export function SelectTemplateModal({
  isOpen, onRequestClose, onSelected, title, userCanCreateTemplates,
  storybookOverrideTemplatesInRequest,
}: Props) {
  const templatesInRequest: MinimalTemplateResponse[] = useSelector(
    (state: StoreShape) => storybookOverrideTemplatesInRequest ?? getTemplates(state),
  );

  const bulkSend = useSelector(isBulkSend);
  const search = useSelector(selectTemplateSearch);
  const intl = useIntl();
  const tmp = useTemplatesWithSearch({ isOpen, includeTemplateLinks: false });
  const handleCreateTemplate = () => {
    trackHeapCustomEvent('Click_Create_Template_P&S_Modal', { button_type: 'v1' });
  };
  const onRequestCloseProxy = () => {
    setIsSearching(false);
    onRequestClose();
  };
  const {
    isLoading,
    isSearching,
    setIsSearching,
    gotoPage,
    isStarZeroState,
    includeTemplateLinks,
  } = tmp;

  const data = tmp.data == null ? undefined : {
    ...tmp.data,
    pageResults: tmp.data?.pageResults.map((t) => {
      const { disabled, message } = isDisabled(
        t.template,
        templatesInRequest,
        bulkSend,
      );
      return {
        ...t,
        disabled,
        message,
      };
    }),
  };


  if (
    !isSearching
    && !isLoading
    && !isStarZeroState
    && data
    && data.resultsCount === 0
  ) {
    return (
      <ModalZeroState
        isOpen={isOpen}
        userCanCreateTemplates={userCanCreateTemplates}
        onRequestClose={onRequestCloseProxy} />
    );
  }

  const showBorder = !isLoading && data && !isStarZeroState;
  const hasData = (data && data.pageResults.length > 0);
  const showDocumentsTable = !isSearching || hasData;

  return (
    <Modal
      open={isOpen}
      isCentered
      withCloseButton={intl.formatMessage(messages.close)}
      onRequestClose={onRequestCloseProxy}
      className={styles.modal}
      width={'large'}
    >
      <Modal.Header hasBottomSpacing="title-small" className={styles.header} >
        <Modal.Title>{title}</Modal.Title>
        <TabsAndSearch
          isSearching={isSearching}
          includeTemplateLinks={includeTemplateLinks}
          setIsSearching={setIsSearching}
          isLoading={isLoading} />
      </Modal.Header>
      <Modal.Body className={styles.body}>
        {!showBorder && (
          <Spacer top={SpacerSize.LG}>
            {isLoading ? (
              <div className={styles.loading} data-qa-ref="loading">
                <Spinner aria-valuetext="Loading..." />
              </div>
            ) : (
              <React.Fragment>
                {isSearching && !data && (
                  <SearchZeroState kind="beforeSearch" />
                )}
                {isStarZeroState && (
                  <StarsZeroState />
                )}
              </React.Fragment>
            )}
          </Spacer>
        )}
        {showBorder && data && !isStarZeroState && (
          <div className={classNames({
            [styles.showBorder]: showDocumentsTable,
            [styles.noResultsPadding]: isSearching && data.pageResults?.length === 0,
            [styles.showBorderTop]: !isLoading && search?.resultsCount && search?.status === 'fulfilled',
          })}
          >
            {isSearching && data.pageResults.length === 0 && (
              <SearchZeroState kind="noResults" />
            )}

            {showDocumentsTable && <DocumentsTable
              data={data}
              showPagination={false}
              currentPage={data.currentPage}
              activeKey={null}
              data-qa-ref="signature-requests-table"
              className={classNames(styles.table)}
              onPageChange={(pageNum) => gotoPage(pageNum)}
              isLoading={isLoading}
              onSelectRow={(id) => {
                if (id) {
                  const templateData = data.pageResults.find((t) => t.guid === id);
                  if (templateData != null && !templateData.disabled) {
                    onSelected(templateData.template);
                  }
                }
              }}
              columnNames={[]}
              columnData={[
                (t) => {
                  if (t.disabled && t.message) {
                    const title = intl.formatMessage(t.message);
                    return (
                      <Tooltip title={title}>
                        <div data-disabled="1" data-tooltip={title}>{t.template.title}</div>
                      </Tooltip>);
                  }
                  return <div>{t.template.title}</div>;
                },
              ]} />}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className={styles.footer}>
        {userCanCreateTemplates && (
          <span className={styles.createNewTemplate}>
            <FormattedMessage
              id=""
              description="Footer text on the select template modal"
              defaultMessage="<a>Create a new template</a>"
              values={{
                // eslint-disable-next-line react/display-name
                a: (message: any) => (
                  <Button variant='transparent' key="create" href="/home/createTemplate" onClick={handleCreateTemplate}>{message}</Button>
                ),
              }} />
          </span>
        )}

        {data && data.numPages > 0 && (
          <Pagination
            className={styles.pagination}
            currentPage={data.currentPage}
            numPages={data.numPages}
            isLoading={isLoading}
            onPageChange={(pageNum) => gotoPage(pageNum)} />
        )}
      </Modal.Footer>
    </Modal>

  );
}


function StarsZeroState() {
  const intl = useIntl();


  return (
    <div className={styles.zeroState}>
      <div className={styles.header}>
        {intl.formatMessage(messages.zeroStateUnstarredHeader)}
      </div>
      <div className={styles.description}>
        <FormattedMessage
          id=''
          description='Text telling user how to star a template to show in the starred template search'
          defaultMessage='On the templates page, click the ⭐️ next to a template to save it for quick access.' />
      </div>
      <div><img src={unstarredZeroState} /></div>
    </div>
  );
}
